import { QueryOperation, SearchQuery } from "../code/search-query/search-query";
import { buildSearchQuery, comboboxValue, dateRangeValue, QueryFieldMapping } from "../code/search-query/query-mapper";
import { DataColumn } from "../controls/Data.default";
import { DynamicSchema } from "../controls/DynamicForm.default";
import { NotificationType } from "../repository/data.default";
import { InputObject } from "../controls/default";

export interface NotificationsViewForm {
  readonly title: string;
  readonly message: string;
  readonly type: InputObject<NotificationType>;
  readonly sentByUid: string;
  readonly sentByUserName: string;
  readonly isSent: boolean;
  readonly sentAt: string;
  readonly scheduledAt: string;
  readonly createdOn: string;
}

export const notificationsViewColumns: DataColumn[] = [
  {
    title: "Title",
    key: "title",
    width: 192,
    sort: true,
    defaultSort: "asc",
    fixed: "left",
  },
  {
    title: "Message",
    key: "message",
    width: 192,
    sort: true,
  },
  {
    title: "Type",
    key: "type",
    width: 144,
    sort: true,
    transform: (value) => {
      return NotificationType[value as NotificationType] != null ? value : "";
    },
  },
  {
    title: "Scheduled At",
    key: "scheduledAt",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  { title: "Is Sent", key: "isSent", width: 92, sort: true, type: "badge" },
  // Recipient roll-up — the at-a-glance "who got / read it" proof from the grid itself.
  { title: "Recipients", key: "recipientsCount", width: 110, sort: true, type: "badge" },
  { title: "Sent", key: "sentCount", width: 88, sort: true, type: "badge" },
  { title: "Delivered", key: "deliveredCount", width: 100, sort: true, type: "badge" },
  { title: "Read", key: "readCount", width: 88, sort: true, type: "badge" },
  {
    title: "Sent By User",
    key: "sentByUserName",
    width: 168,
    sort: true,
  },
  {
    title: "Sent At",
    key: "sentAt",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
  {
    title: "Created On",
    key: "createdOn",
    width: 168,
    sort: true,
    format: "DD/MM/YYYY HH:mm",
  },
];

export const notificationsSearchSchema: DynamicSchema = {
  groups: [
    {
      controls: [
        { type: "textbox", name: "title", label: "Title" },
        { type: "textbox", name: "message", label: "Message" },
        {
          type: "combobox",
          name: "type",
          label: "Type",
          request: () =>
            Promise.resolve(
              Object.entries(NotificationType).map(([value, display]) => ({
                value,
                display,
              })),
            ),
        },
        { type: "rangetimepicker", name: "scheduledAt", label: "Scheduled At" },
        { type: "togglebox", name: "isSent", label: "Is Sent" },
        { type: "textbox", name: "sentByUserName", label: "Sent By User" },
        { type: "rangetimepicker", name: "sentAt", label: "Sent At" },
        { type: "rangetimepicker", name: "createdOn", label: "Created On" },
      ],
    },
  ],
};

const notificationsQueryMappings: QueryFieldMapping[] = [
  { formField: "title" },
  { formField: "message" },
  { formField: "type", operation: QueryOperation.Equal, transform: comboboxValue },
  { formField: "scheduledAt", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "isSent", operation: QueryOperation.Equal },
  { formField: "sentByUserName" },
  { formField: "sentAt", operation: QueryOperation.Between, transform: dateRangeValue },
  { formField: "createdOn", operation: QueryOperation.Between, transform: dateRangeValue },
];

export const mapNotificationsViewQuery = (values: NotificationsViewForm): SearchQuery =>
  buildSearchQuery(values, notificationsQueryMappings);
